import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useReducedMotion, useSpring } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

const ROUTE_MAP = {
  "#kontakt": "/kontakt",
  "#leistungen": "/leistungen",
  "#referenzen": "/referenzen",
  "#ueber-uns": "/ueber-uns",
  "#start": "/",
};

export const scrollToId = (id) => {
  const el = document.querySelector(id);
  if (el) {
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -96, duration: 1.2 });
    else el.scrollIntoView({ behavior: "smooth" });
    return;
  }
  if (window.__navigate && ROUTE_MAP[id]) window.__navigate(ROUTE_MAP[id]);
};

export const prefillService = (value) => {
  sessionStorage.setItem("berglar:prefill", value);
  window.dispatchEvent(new CustomEvent("berglar:prefill", { detail: value }));
  scrollToId("#kontakt");
};

export const useTitle = (title) => {
  useEffect(() => {
    document.title = title;
  }, [title]);
};

export const Reveal = ({ children, delay = 0, y = 28, className = "" }) => {
  const reduced = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduced ? { opacity: 0 } : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: reduced ? 0.01 : 0.7, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
};

export const Magnetic = ({ children, strength = 20, className = "" }) => {
  const reduced = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 16, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 180, damping: 16, mass: 0.4 });
  const ref = useRef(null);

  if (reduced) return <div className={`inline-block ${className}`}>{children}</div>;

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    x.set(((e.clientX - r.left) / r.width - 0.5) * strength);
    y.set(((e.clientY - r.top) / r.height - 0.5) * strength);
  };
  const reset = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseEnter={reset}
      onMouseLeave={reset}
      style={{ x: sx, y: sy }}
      className={`inline-block ${className}`}
    >
      {children}
    </motion.div>
  );
};

export const CountUp = ({ value, className = "" }) => {
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const match = String(value).match(/^(\d+)(.*)$/);
  const target = match ? parseInt(match[1], 10) : 0;
  const suffix = match ? match[2] : "";
  const [display, setDisplay] = useState(reduced ? target : 0);

  useEffect(() => {
    if (reduced) {
      setDisplay(target);
      return;
    }
    if (!inView) return;
    const duration = 1400;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, reduced]);

  return (
    <span ref={ref} className={`tabular-nums ${className}`} aria-label={`${target}${suffix}`} role="text">
      {display}
      {suffix}
    </span>
  );
};

export const Kicker = ({ children, dark = false, className = "" }) => (
  <p className={`${dark ? "kicker-dark" : "kicker"} ${className}`}>{children}</p>
);

export const SectionHead = ({ kicker, title, text, testId, dark = false }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 md:mb-16">
    <div>
      <Reveal>
        <Kicker dark={dark}>{kicker}</Kicker>
      </Reveal>
      <Reveal delay={0.08}>
        <h2
          data-testid={testId}
          className={`font-display text-4xl sm:text-5xl font-semibold tracking-tight text-balance leading-[1.08] mt-4 ${
            dark ? "text-white" : "text-ink"
          }`}
        >
          {title}
        </h2>
      </Reveal>
    </div>
    {text && (
      <Reveal delay={0.16} className="max-w-md">
        <p className={`leading-relaxed ${dark ? "text-white/65" : "text-muted"}`}>{text}</p>
      </Reveal>
    )}
  </div>
);
